import React, { useState } from "react";
import { PaperPlaneTilt, CheckCircle } from "@phosphor-icons/react";
import { portalApi } from "@/lib/portalApi";
import { BRAND } from "./portalBrand";
import PortalAuthShell, { authCard, authLabel, authInput, authButton } from "./PortalAuthShell";

/**
 * "Request access" — a visitor asks for a portal login.
 *
 * This creates NO account and grants NO access: it files a pending request that a
 * staff member must approve in the ERP and link to a customer record, which is what
 * finally issues the login. That link is what scopes a customer to their own
 * shipments, so it can't be self-asserted here. The backend answers generically,
 * so this screen must not imply whether the address is already known.
 */
export default function PortalRequestAccess() {
  const [form, setForm] = useState({ company_name: "", contact_name: "", email: "", phone: "", message: "" });
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await portalApi.post("/portal/access-requests", {
        company_name: form.company_name.trim(),
        contact_name: form.contact_name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim() || null,
        message: form.message.trim() || null,
      });
    } catch {
      /* Generic by design — see the note above. */
    } finally {
      setBusy(false);
      setSent(true);
    }
  };

  if (sent) {
    return (
      <PortalAuthShell title="Request received" subtitle="We'll be in touch by email.">
        <div className={authCard} data-testid="portal-access-sent">
          <div className="flex items-start gap-3">
            <CheckCircle size={20} weight="fill" className="text-emerald-500 mt-0.5 shrink-0" />
            <div className="text-sm text-slate-600">
              <p>Thanks — our team will review your request and email <span className="font-semibold text-slate-900">{form.email.trim()}</span> once your access is ready.</p>
              <p className="mt-2">Accounts are linked to your company's records by hand, so this usually takes one business day.</p>
            </div>
          </div>
        </div>
      </PortalAuthShell>
    );
  }

  return (
    <PortalAuthShell title="Request portal access" subtitle={`Already a ${BRAND.name} customer? Ask us for a login.`}>
      <form onSubmit={submit} className={authCard}>
        <div>
          <label className={authLabel}>Company name *</label>
          <input value={form.company_name} onChange={(e) => set("company_name", e.target.value)} required autoFocus
            data-testid="portal-access-company" className={authInput} placeholder="Your company" />
        </div>
        <div>
          <label className={authLabel}>Your name *</label>
          <input value={form.contact_name} onChange={(e) => set("contact_name", e.target.value)} required
            data-testid="portal-access-name" className={authInput} placeholder="Full name" />
        </div>
        <div>
          <label className={authLabel}>Work email *</label>
          <input type="email" value={form.email} onChange={(e) => set("email", e.target.value)} required
            data-testid="portal-access-email" className={authInput} placeholder="you@company.com" />
        </div>
        <div>
          <label className={authLabel}>Phone</label>
          <input value={form.phone} onChange={(e) => set("phone", e.target.value)}
            data-testid="portal-access-phone" className={authInput} placeholder="+91 …" />
        </div>
        <div>
          <label className={authLabel}>Anything we should know?</label>
          <textarea value={form.message} onChange={(e) => set("message", e.target.value)} rows={3}
            data-testid="portal-access-message" className={authInput + " h-auto py-2"}
            placeholder="e.g. a recent booking or BL number, so we can match your account" />
        </div>
        <button type="submit" disabled={busy} data-testid="portal-access-btn" className={authButton}>
          <PaperPlaneTilt size={16} /> {busy ? "Sending…" : "Request access"}
        </button>
      </form>
    </PortalAuthShell>
  );
}
